// ═══════════════════════════════════════════════
//   PERSISTENCE — declarative registry of fields persisted to Supabase.
//   Loaded after state.js, before db.js.
//
//   Adding a new persisted field = one entry here + one ALTER TABLE.
//   loadProfile/pushSave/doLogout all derive from this registry.
//
//   Field spec:
//     key            S.<key> (or external target if `external`)
//     col            profiles.<col> (or pseudo for composite)
//     def            factory that returns the default value
//     load(raw, row) optional: deserialize DB value → S value
//     save(val)      optional: serialize S value → DB value
//     resetOnLogout  default true
//     readOnly       skip in save payload (set by trigger, etc.)
//     external       value lives on window, not S; needs read/write
//     composite      maps to multiple columns; needs loadFromRow + saveToRow
//     sideEffect(v)  runs in a second pass after every field is loaded
//     localOnly      skip in both load and save — value is hydrated by
//                    loadProfile from a sidecar table instead. Still reset
//                    on logout via the registry so the field can't drift.
// ═══════════════════════════════════════════════

const PERSISTED_FIELDS = [];

function registerField(spec) {
    PERSISTED_FIELDS.push(spec);
}

// ── Scalar / array / jsonb fields ──
registerField({ key:'pts',                  col:'pts',                    def: () => 0 });
// Login bookkeeping for the games-window NEW / UPD badges. lastLoginAt is
// updated to Date.now() on every login; prevLoginAt holds the value from
// the login before that and is what the badge logic compares against.
registerField({ key:'lastLoginAt',           col:'last_login_at',          def: () => 0 });
registerField({ key:'prevLoginAt',           col:'prev_login_at',          def: () => 0 });
// myAliens lives in the user_aliens table; loadProfile rebuilds it from rows.
registerField({ key:'myAliens',             def: () => [], localOnly: true });
// unlockedBgs / unlockedNicks live in user_unlocks; loadProfile rebuilds them.
registerField({ key:'unlockedBgs',          def: () => ['hills'],         localOnly: true });
registerField({ key:'unlockedNicks',        def: () => ['chronically'],   localOnly: true });
// ownedIrl lives in user_irl_codes; loadProfile rebuilds it.
registerField({ key:'ownedIrl',             def: () => [],                localOnly: true });
registerField({ key:'activeBg',             col:'active_bg',              def: () => 'hills' });
// completedMissions + missionLinkOpened live in user_missions.
registerField({ key:'completedMissions',    def: () => [],                localOnly: true });
registerField({ key:'missionLinkOpened',    def: () => ({}),              localOnly: true });
// dailyState lives in user_daily_state; loadProfile rebuilds it.
registerField({
    key: 'dailyState',
    def: () => ({ claimed: [], lastDate: '', finaleClaimed: false }),
    localOnly: true,
});
registerField({ key:'gamesPlayed',          col:'games_played',
    def:  () => new Set(),
    load: (raw) => new Set(raw),
    save: (val) => [...val]
});
// Tama settings live in user_tama_settings.
registerField({ key:'activeTamaUids',       def: () => [],                localOnly: true });
registerField({ key:'maxTamaSlots',         def: () => 2,                 localOnly: true });
// gameTopScores lives in user_game_scores; loadProfile rebuilds the {key:score} map.
registerField({ key:'gameTopScores',        def: () => ({}),              localOnly: true });
registerField({ key:'gameLastPlayed',       col:'game_last_played',       def: () => ({}) });
// myToys lives in user_toys; loadProfile rebuilds it.
registerField({ key:'myToys',               def: () => [],                localOnly: true });
// myBunnymeals lives in user_bunnymeals; loadProfile rebuilds it.
registerField({ key:'myBunnymeals',         def: () => [],                localOnly: true });
// Quiz state lives in user_quiz.
registerField({ key:'clowriQuizDone',       def: () => false,             localOnly: true });
registerField({ key:'clowriQuizScore',      def: () => 0,                 localOnly: true });
// Bakery state lives in user_bakery.
registerField({ key:'bakeryUpgrades',       def: () => ({ speed:0, oven:0, grace:0, yield:0, autostart:0, lucky:0, shelf:0, counter:0, charm:0, deals:0, loyalty:0, decor:0, wallpaper:0, chef:0, sparkle:0 }), localOnly: true });
registerField({ key:'bakeryBakeCount',      def: () => 0,                                                                                                               localOnly: true });
registerField({ key:'bakeryUnlocked',       def: () => ['cookie'],                                                                                                      localOnly: true });
registerField({ key:'bakeryOvens',          def: () => [0,1,2,3,4].map(() => ({ started:null, goodie:'cookie', sel:'cookie' })),                                        localOnly: true });
registerField({ key:'bakeryInventory',      def: () => ({}),                                                                                                            localOnly: true });
registerField({ key:'bakeryCoins',          def: () => 0,                                                                                                               localOnly: true });
// Logbook favorites live in user_logbook_favorites (one row per fav).
registerField({ key:'logbookFavorites',     def: () => [],                localOnly: true });
// Mail state lives in user_mail.
registerField({ key:'mailFirstOpen',        def: () => null,              localOnly: true });
registerField({ key:'mailStoryRead',        def: () => [],                localOnly: true });
registerField({ key:'mailStoryChoices',     def: () => ({}),              localOnly: true });
registerField({ key:'mailStoryTrashed',     def: () => [],                localOnly: true });
registerField({ key:'mailDailyState',       def: defaultMailDailyState,   localOnly: true });
registerField({ key:'mailImportantTrashed', def: () => [],                localOnly: true });
registerField({ key:'mailSpamState',        def: defaultMailSpamState,    localOnly: true });
// v3 calendar arc — all hydrated from user_mail_state in loadProfile.
registerField({ key:'mailShopHealth',       def: () => 70,                localOnly: true });
registerField({ key:'mailClowAwareness',    def: () => 0,                 localOnly: true });
registerField({ key:'mailMysteryDepth',     def: () => 0,                 localOnly: true });
registerField({ key:'mailRecordsKept',      def: () => 0,                 localOnly: true });
registerField({ key:'mailTrustHideo',       def: () => 'mid',             localOnly: true });
registerField({ key:'mailTrustPetra',       def: () => 'mid',             localOnly: true });
registerField({ key:'mailLastActiveDay',    def: () => 0,                 localOnly: true });
registerField({ key:'mailFirstLoginDay',    def: () => null,              localOnly: true });
registerField({ key:'mailEmailStates',      def: () => ({}),              localOnly: true });
registerField({ key:'mailEnding',           def: () => null,              localOnly: true });
// Writing-spec v1 — voice counters, recovery flag, story flags, packagePath.
// All hydrated from user_mail_state in loadProfile (sidecar columns).
registerField({ key:'playerVoiceWarm',         def: () => 0,     localOnly: true });
registerField({ key:'playerVoiceCurious',      def: () => 0,     localOnly: true });
registerField({ key:'playerVoiceProfessional', def: () => 0,     localOnly: true });
registerField({ key:'playerVoiceClipped',      def: () => 0,     localOnly: true });
registerField({ key:'clippedRecoveryActive',   def: () => false, localOnly: true });
registerField({ key:'eMorrowSurfaced',         def: () => false, localOnly: true });
registerField({ key:'documentedPath',          def: () => false, localOnly: true });
registerField({ key:'hideoRouteSurfaced',      def: () => false, localOnly: true });
registerField({ key:'packagePath',             def: () => null,  localOnly: true });
// Writing spec v2 — staff signals + five named records.
registerField({ key:'staffConcern',            def: () => 0,           localOnly: true });
registerField({ key:'staffConcernEvents',      def: () => new Set(),   localOnly: true });
registerField({ key:'record_package',          def: () => false,       localOnly: true });
registerField({ key:'record_tall_customer',    def: () => false,       localOnly: true });
registerField({ key:'record_emorrow',          def: () => false,       localOnly: true });
registerField({ key:'record_hideo_ps',         def: () => false,       localOnly: true });
registerField({ key:'record_corridors',        def: () => false,       localOnly: true });
// Writing spec v3 §19 — Hideo romance opt-in. Set-only via d04 choice C.
registerField({ key:'hideoRomance',             def: () => false,       localOnly: true });
// Writing spec v6 §01 — Petra romance opt-in. Set-only via d07 choice B.
registerField({ key:'petraRomance',             def: () => false,       localOnly: true });
// Writing spec v5 — drops recovery arc (Master Canon v2 §06A). Both hydrated
// from user_mail_state in loadProfile (sidecar columns).
registerField({ key:'dropsPulled',              def: () => false,       localOnly: true });
registerField({ key:'dropsPulledBy',            def: () => null,        localOnly: true });

// ── Composite: profileData maps to 6 columns ──
registerField({
    key: 'profileData',
    composite: true,
    def: () => ({ name:'USER', bio:'', status:'', nick:'', traits:[], headerColors:{nameFill:'',nameStroke:'',tagBg:'',headerBg:''} }),
    loadFromRow: (data) => ({
        name: data.profile_name || data.username || 'USER',
        bio: data.profile_bio || '',
        status: data.profile_status || '',
        nick: data.profile_nick || '',
        traits: data.profile_traits || [],
        headerColors: data.profile_header_colors || {nameFill:'',nameStroke:'',tagBg:'',headerBg:''}
    }),
    saveToRow: (val) => ({
        profile_name: val.name,
        profile_bio: val.bio,
        profile_status: val.status || '',
        profile_nick: val.nick,
        profile_traits: val.traits,
        profile_header_colors: val.headerColors || {nameFill:'',nameStroke:'',tagBg:''}
    })
});

// ── External: prof_icon_idx lives on window, not S ──
registerField({
    key: 'profIconIdx',
    col: 'prof_icon_idx',
    def: () => 1,
    external: true,
    read:  () => window.profIconIdx,
    write: (v) => { window.profIconIdx = v; }
});

// ── Staff hiring minigame ──
registerField({ key:'staffHiredSlots',    col:'staff_hired_slots',  def: () => ({}) });
registerField({ key:'staffRejectedNames', col:'staff_rejected',     def: () => [] });
registerField({ key:'staffDailyDate',     col:'staff_daily_date',   def: () => '' });
registerField({ key:'staffDailyCount',    col:'staff_daily_count',  def: () => 0 });

// ── ReadOnly: early_adopter is set by a trigger; mirrored into S for UI ──
registerField({
    key: 'data_early_adopter_flag',
    col: 'early_adopter',
    def: () => false,
    readOnly: true,
    load: (raw) => !!raw,
    sideEffect: (val) => {
        if (val && !S.unlockedNicks.includes('early_bird')) S.unlockedNicks.push('early_bird');
    }
});

// ═══════════════════════════════════════════════
//   Apply, build, reset
// ═══════════════════════════════════════════════

function _writeField(f, val) {
    if (f.external) f.write(val);
    else S[f.key] = val;
}

function _readField(f) {
    if (f.external) return f.read();
    return S[f.key];
}

function applyLoadedRow(data) {
    // Pass 1: load every field. localOnly fields keep their default — loadProfile
    // hydrates them from sidecar tables in a follow-up step.
    for (const f of PERSISTED_FIELDS) {
        let val;
        if (f.localOnly) {
            val = f.def();
        } else if (f.composite) {
            val = f.loadFromRow(data);
        } else {
            const raw = data[f.col];
            val = (raw === null || raw === undefined) ? f.def() : (f.load ? f.load(raw, data) : raw);
        }
        _writeField(f, val);
    }
    // Pass 2: side effects (run after every value is in place)
    for (const f of PERSISTED_FIELDS) {
        if (!f.sideEffect) continue;
        f.sideEffect(_readField(f), data);
    }
}

function buildSavePayload() {
    const out = {};
    for (const f of PERSISTED_FIELDS) {
        if (f.readOnly || f.localOnly) continue;
        if (f.composite) {
            Object.assign(out, f.saveToRow(S[f.key]));
            continue;
        }
        const val = _readField(f);
        out[f.col] = f.save ? f.save(val) : val;
    }
    return out;
}

function resetPersistedState() {
    for (const f of PERSISTED_FIELDS) {
        if (f.resetOnLogout === false) continue;
        _writeField(f, f.def());
    }
}

window.PERSISTED_FIELDS = PERSISTED_FIELDS;
window.applyLoadedRow = applyLoadedRow;
window.buildSavePayload = buildSavePayload;
window.resetPersistedState = resetPersistedState;
